import { useEffect, useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { callLeaderboardCompetitionWishlist, COMPETITIONS_LEADERBOARD_CACHE } from '@app/client/resources/competitions';
import { useFantasyLeaderboardStore } from '@app/store/zustand';

import { getNextPageParam } from '../../../deprecated';
import { useGameSlug } from '../../../fantasyWishlists';

const DEFAULT_LIMIT = 100;
enum LeaderboardQueryFilters {
    TeamId = 'team_id',
    UsernameQuery = 'query',
    UserId = 'user_id',
}

type UseGetCompetitionLeaderboardProps = {
    slug?: string;
    teamId?: string;
    query?: string;
    userId?: string;
    disabled?: boolean;
    interval?: boolean;
    page?: number;
    limit?: number;
    ignoreSignal?: boolean;
};
export const useGetCompetitionLeaderboard = (props?: UseGetCompetitionLeaderboardProps) => {
    const {
        disabled,
        slug: providedSlug,
        teamId,
        query,
        interval,
        page = 0,
        limit = DEFAULT_LIMIT,
        userId,
        ignoreSignal = false,
    } = props || {};

    const { setLeaderboardKey, leaderboardKey } = useFantasyLeaderboardStore();

    const gameSlug = useGameSlug();
    const slug = providedSlug || gameSlug;
    const cache = useMemo(
        () => [COMPETITIONS_LEADERBOARD_CACHE, slug, teamId, query, page, limit, userId, disabled],
        [page, query, slug, teamId, limit, userId, disabled],
    );

    useEffect(() => {
        if (!userId && !isEqual(cache, leaderboardKey)) {
            setLeaderboardKey(cache);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cache, userId]);

    const filters = {};
    if (teamId) filters[LeaderboardQueryFilters.TeamId] = teamId;
    if (query) filters[LeaderboardQueryFilters.UsernameQuery] = query;
    if (userId) filters[LeaderboardQueryFilters.UserId] = userId;

    return useInfiniteQuery(
        cache,
        ({ signal, pageParam = page }) => {
            return callLeaderboardCompetitionWishlist({
                slug,
                settings: { signal: ignoreSignal ? undefined : signal },
                query: {
                    ...filters,
                    page: pageParam || 0,
                    limit,
                },
            });
        },
        {
            enabled: Boolean(slug) && !disabled,
            getNextPageParam,
            keepPreviousData: false,
            retry: false,
            notifyOnChangeProps: 'all',
            refetchInterval: interval ? 5000 : undefined,
        },
    );
};
